import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { publicApi } from '../../services/publicApi';

const fallbackProject = {
  title: 'Riyo POS System',
  description:
    'A powerful, intuitive, and modern Point of Sale system designed to streamline business operations, manage inventory, and drive growth.',
  image: '/img/riyo pos.png',
};

const FeaturedProject = () => {
  const [project, setProject] = useState(fallbackProject);

  useEffect(() => {
    let isMounted = true;
    const loadFeatured = async () => {
      try {
        const data = await publicApi.getProjects();
        if (!Array.isArray(data) || !isMounted) return;

        const featured = data.find((item) => item.isFeatured && item.isActive !== false);
        if (!featured) return;

        setProject({
          title: featured.title,
          description: featured.description,
          image: featured.mainImageUrl || fallbackProject.image,
        });
      } catch {
        // Keep static fallback.
      }
    };

    loadFeatured();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section className="relative pt-4 pb-24 bg-white dark:bg-black overflow-hidden transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-4 inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-widest uppercase">
              Featured Project
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-black dark:text-white mb-4">
              {project.title}
            </h2>
            <p className="text-gray-500 dark:text-gray-300 max-w-2xl mx-auto text-lg">
              {project.description}
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative max-w-5xl mx-auto"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/15 via-primary/25 to-primary/10 dark:from-primary/20 dark:via-primary/30 dark:to-primary/15 blur-3xl rounded-full transform scale-110"></div>

          <div className="relative rounded-2xl md:rounded-[2rem] overflow-hidden bg-white/50 dark:bg-white/5 backdrop-blur-sm border border-white/60 dark:border-white/10 p-2 md:p-4 shadow-[0_20px_60px_rgba(0,0,0,0.1)]">
            <img
              src={project.image}
              alt={`${project.title} UI`}
              className="w-full h-auto rounded-xl md:rounded-3xl border border-gray-100 dark:border-white/10 shadow-sm"
            />
          </div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 }}
            className="absolute -left-4 md:-left-8 top-1/4 bg-white dark:bg-[#111111] px-4 py-3 rounded-2xl shadow-xl flex items-center gap-3 border border-gray-50 dark:border-white/10 z-20"
          >
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Status</div>
              <div className="text-sm font-bold text-black dark:text-white">Live Project</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedProject;
